import React, { useState } from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { AxiosError, AxiosResponse } from "axios";
import { postRequest } from "../../../../utils/httpRequest";
import { notification } from "../../../../utils/helpers";

export interface Card {
  id: string;
  brand: string;
  exp_month: number;
  exp_year: number;
  last4: string;
}

interface Props {
  card?: Card;
  onCancel: () => void;
  onSaved: (card: Card) => void;
}

const stripeStyles = {
  base: {
    color: "#2E2437",
    fontFamily: '"Open Sans", sans-serif',
    fontSize: "14px",
    fontSmoothing: "antialiased",
    "::placeholder": {
      color: "#8A7F91",
    },
  },
  invalid: {
    color: "#dc2626",
  },
};

const formatBrand = (brand) => {
  if (!brand) return "CARD";
  return brand.toUpperCase();
};

const CardOnFile = ({ card }: { card?: Card }) => {
  if (!card) {
    return (
      <div className="card-on-file">
        <span className="card-brand">CARD</span>
        No card on file
      </div>
    );
  }

  return (
    <div className="card-on-file">
      <span className="card-brand">{formatBrand(card.brand)}</span>
      <span className="card-dots" aria-hidden="true">
        &bull;&bull;&bull;
      </span>
      {card.last4}
      <span className="card-exp">
        Expires {card.exp_month}/{card.exp_year}
      </span>
    </div>
  );
};

const CardEditForm: React.FC<Props> = ({ card, onCancel, onSaved }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    if (!stripe || !elements) {
      setSubmitting(false);
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    const payload = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });

    if (payload.error) {
      setError(payload.error.message);
      setSubmitting(false);
      return;
    }

    postRequest("/api/payment_methods.json", {
      payment_method_id: payload.paymentMethod.id,
    })
      .then((response: AxiosResponse<Card>) => {
        onSaved(response.data);
        notification("Card details were successfully updated.", "notification");
      })
      .catch((requestError: AxiosError<{ error: string }>) => {
        setError(
          requestError?.response?.data?.error ||
            "Unable to update card details.",
        );
        setSubmitting(false);
      });
  };

  return (
    <form className="card-edit" id="cardEdit" onSubmit={handleSubmit}>
      <CardOnFile card={card} />
      <div className="set-field">
        <label htmlFor="cardNum">Card number</label>
        <div className="set-input card-element" id="cardNum">
          <CardNumberElement
            options={{ style: stripeStyles }}
            onChange={() => setError("")}
          />
        </div>
      </div>
      <div className="set-row2">
        <div className="set-field">
          <label htmlFor="cardExp">Expiry</label>
          <div className="set-input card-element" id="cardExp">
            <CardExpiryElement
              options={{ style: stripeStyles }}
              onChange={() => setError("")}
            />
          </div>
        </div>
        <div className="set-field">
          <label htmlFor="cardCvc">CVC</label>
          <div className="set-input card-element" id="cardCvc">
            <CardCvcElement
              options={{ style: stripeStyles }}
              onChange={() => setError("")}
            />
          </div>
        </div>
      </div>
      {error && <div className="set-error">{error}</div>}
      <div className="card-edit-actions">
        <button
          type="submit"
          className={`btn btn-primary btn--sm ${submitting ? "loading" : ""}`}
          disabled={!stripe || submitting}
        >
          Save card
        </button>
        <button
          type="button"
          className="btn btn-ghost btn--sm"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
      <p className="card-secure">
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M4 7 V5 a4 4 0 0 1 8 0 V7 M3 7 h10 v6 a1 1 0 0 1 -1 1 H4 a1 1 0 0 1 -1 -1 z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
        </svg>
        Encrypted and processed securely. We never store full card numbers.
      </p>
    </form>
  );
};

export default CardEditForm;
